import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import UEFieldSearch from "../../misc/UEFieldSearch";
import FlowControlFieldImport from "../../misc/FlowControlFieldImport";
import MaterialModal from "../MaterialModal";
import { FormControl, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { hideModal } from "../../../actions/actionCreators";
import {
    setEnrichmentFields,
    validateUEFieldsCompliance,
    setValidatedImportFields,
    setSelectedEnrichmentFieldsItem,
} from "../../../actions/flowControlActions";
import { getFlowExternalServiceParametersForSelectedFlowItem } from "../../../reducers/flowExternalServiceParameters";
import { updateAttribute } from "../../../actions/flowActions";
import { IAppState } from "../../../types/stores";
import { IEnrichmentField, IEnrichmentFieldImport } from "../../../types/stores/umbrellaData";
import { FlowExternalServiceParameter } from "../../../types/stores/flowTypes";
import { IExternalParameter } from "../../../types/stores/flowControlData";
import withModal from "../withModal";

const ueFieldSearchModalProps = {
    isOpen: PropTypes.bool,
    // Injected from Redux (ActionCreators)
    hideModal: PropTypes.func,
};

type UEFieldSearchModalProps = PropTypes.InferProps<typeof ueFieldSearchModalProps>;

const UEFieldSearchModal: React.FC<UEFieldSearchModalProps> = ({ isOpen }: UEFieldSearchModalProps) => {
    const dispatch = useDispatch();

    const [addType, setAddType] = useState("search");
    const [excludeErrors, setExcludeErrors] = useState(false);

    const flowServiceParameters = useSelector<IAppState, Array<FlowExternalServiceParameter>>(state =>
        getFlowExternalServiceParametersForSelectedFlowItem(state)
    );
    const flowServiceId = flowServiceParameters && flowServiceParameters[0] ? flowServiceParameters[0].ServiceId : 0;
    const externalParameters = useSelector<IAppState, Array<IExternalParameter>>(
        state => state.flowControlData.externalParameters
    );
    const validatedFields = useSelector<IAppState, Array<IEnrichmentFieldImport>>(
        state => state.umbrellaData.validatedFields
    );
    const selectedFields = useSelector<IAppState, Array<IEnrichmentField>>(
        state => state.umbrellaData.selectedFieldsItem
    );
    const selectedFlowItem = useSelector((state: IAppState) => state.selected.flowItem);

    const hide = () => {
        setAddType("search");
        dispatch(setEnrichmentFields([]));
        dispatch(setValidatedImportFields([]));
        dispatch(hideModal("UE_SEARCH_FIELDS"));
        dispatch(validateUEFieldsCompliance(true));
        setAddType("search");
        setExcludeErrors(false);
    };

    const importFields = () => {
        const validFields = validatedFields.filter(x => x.IsValid);
        const enrichmentFields = validFields.map((x: any) => x.EnrichmentField);
        const uniqueFields = [...new Map(enrichmentFields.map(x => [x["FieldId"], x])).values()];
        let itemFields = [...selectedFields];
        // Add to selectedFields
        uniqueFields.forEach(x => {
            itemFields.push(x);
        });
        dispatch(setSelectedEnrichmentFieldsItem(itemFields));

        // Always Person 1
        let fields = validFields.filter(x => x.Person == 1).map(x => x.EnrichmentField.FieldId);
        updateServiceParameter(fields.join(","), ueParmName(1));

        // Additional Persons
        for (let i = 2; i < 9; i++) {
            fields = validFields.filter(x => x.Person == i).map(x => x.EnrichmentField.FieldId);
            if (fields.length > 0) {
                updateServiceParameter(fields.join(","), ueParmName(i));
            }
        }

        hide();
    };

    const ueParmName = (person: number) => {
        let parmName = "";
        switch (person) {
            case 1:
                parmName = "Field_List";
                break;
            default:
                parmName = "adult_" + person.toString();
                break;
        }
        return parmName;
    };

    const updateServiceParameter = (fieldIds, parameterName) => {
        const fieldList = externalParameters.find(x => x.ParameterName == parameterName);
        const itemParameter = flowServiceParameters.find(x => x.ParameterId == fieldList?.ParameterId);
        let existingValues: Array<string> = [];
        const newValues = fieldIds.split(",");

        if (itemParameter!.Value.length > 0) {
            existingValues = itemParameter!.Value.split(",");
        }

        newValues.forEach(fieldId => {
            if (!existingValues.includes(fieldId)) {
                existingValues.push(fieldId);
            }
        });

        let sortedValues = existingValues.map(x => parseInt(x));
        sortedValues.sort((a, b) => a - b);

        dispatch(
            updateAttribute(
                "flowExternalServiceParameters",
                itemParameter!.FlowServiceParameterId,
                "Value",
                sortedValues.length == 1 ? sortedValues[0].toString() : sortedValues.join(",")
            )
        );
    };

    const disableImport =
        (!validatedFields || validatedFields.length == 0 || validatedFields.filter(x => !x.IsValid).length > 0) &&
        !excludeErrors;

    return (
        <MaterialModal
            id="search-ue-fields"
            title="Universal Enrichment Fields"
            maxWidth="lg"
            fullWidth={true}
            isOpen={Boolean(isOpen)}
            onClose={hide}
            cancelButtonText="Close"
            showPrimaryButton={addType == "import"}
            primaryButtonText="Import"
            onPrimaryButtonRequest={importFields}
            isPrimaryButtonDisabled={disableImport}
        >
            <div>
                <div>
                    <FormControl>
                        <RadioGroup row name="type" value={addType} onChange={e => setAddType(e.target.value)}>
                            <div style={{ marginBottom: "10px" }}>
                                <FormControlLabel value="search" control={<Radio color="primary" />} label="Search" />
                                <FormControlLabel
                                    value="import"
                                    control={<Radio color="primary" />}
                                    label="Import CSV"
                                />
                            </div>
                        </RadioGroup>
                    </FormControl>
                </div>
                {isOpen ? (
                    addType == "search" ? (
                        <UEFieldSearch flowItemId={selectedFlowItem} flowServiceId={flowServiceId} />
                    ) : (
                        <FlowControlFieldImport
                            importType="ue"
                            excludeErrors={excludeErrors}
                            excludeErrorsChanged={setExcludeErrors}
                            importedLayoutFields={[]}
                            updateFields={() => null}
                            layoutFixed={false}
                            setLayoutFixed={() => null}
                        />
                    )
                ) : null}
            </div>
        </MaterialModal>
    );
};

export default withModal(UEFieldSearchModal, "UE_SEARCH_FIELDS");
